// The app's icons, in one place. Screens import them from `@ims/ui/icons` by what they mean
// here (the names the code has always used); this file decides which Phosphor drawing each
// one is. Phosphor draws two-tone ("duotone") where an `IconContext` asks for it (the app
// shell does); the small glyphs at the bottom stay plain lines at any weight.
import type { Icon, IconProps } from '@phosphor-icons/react'
import type { ReactNode } from 'react'
import {
  ArrowCounterClockwise,
  ArrowDown as PhArrowDown,
  ArrowLeft as PhArrowLeft,
  ArrowRight as PhArrowRight,
  ArrowSquareOut,
  ArrowUp as PhArrowUp,
  ArrowsClockwise,
  ArrowsOut,
  CaretDown,
  CaretLeft,
  CaretRight,
  CaretUp,
  Check as PhCheck,
  CircleNotch,
  DotsSixVertical,
  DotsThree,
  IconContext,
  Minus as PhMinus,
  Plus as PhPlus,
  X as PhX,
} from '@phosphor-icons/react'

export type IconType = Icon

const TWO_TONE = { weight: 'duotone' } as const

/** Draws every icon inside it two-tone. The app wraps itself in this once. */
export function TwoToneIcons({ children }: { children: ReactNode }) {
  return <IconContext.Provider value={TWO_TONE}>{children}</IconContext.Provider>
}

export {
  Warning as AlertTriangle,
  WarningCircle as AlertCircle,
  Prohibit as Ban,
  ChartBar as BarChart3,
  Pulse as Activity,
  ArrowsLeftRight,
  ArrowsSplit,
  Money as Banknote,
  Bell,
  BellRinging as BellRing,
  Moped as Bike,
  BookOpenText,
  Stack as Boxes,
  Buildings as Building2,
  CalendarCheck,
  CalendarPlus,
  Camera,
  CashRegister,
  CheckCircle as CheckCircle2,
  ChefHat,
  Circle,
  ClipboardText as ClipboardCheck,
  ListChecks as ClipboardList,
  Clipboard as ClipboardPaste,
  Clock,
  CloudWarning as CloudAlert,
  CloudSlash as CloudOff,
  Coffee,
  CookingPot,
  Copy,
  CreditCard,
  Bread as Croissant,
  Database,
  DownloadSimple as Download,
  Eye,
  EyeSlash as EyeOff,
  NotePencil as FilePen,
  FileCsv,
  FilePdf,
  FileXls as FileSpreadsheet,
  Receipt as FileText,
  Lightning as Flashlight,
  LightningSlash as FlashlightOff,
  Gauge,
  Gift,
  FolderSimple,
  Hammer,
  Globe,
  HandCoins,
  Hash,
  House as Home,
  HourglassMedium as Hourglass,
  Power,
  Tag,
  ClockCounterClockwise as History,
  ImageSquare as ImagePlus,
  Hand,
  Info,
  Key as KeyRound,
  Keyboard,
  StackSimple as Layers,
  SquaresFour as LayoutDashboard,
  Lifebuoy as LifeBuoy,
  Lightbulb,
  Link as Link2,
  ListChecks,
  LockKey as LockKeyhole,
  SignOut as LogOut,
  Envelope as Mail,
  MapPin,
  List as Menu,
  ChatText as MessageSquare,
  Monitor,
  Devices as MonitorSmartphone,
  Moon,
  MoonStars,
  Package,
  Pepper,
  BoxArrowDown as PackageCheck,
  PlusSquare as PackagePlus,
  SidebarSimple as PanelLeft,
  PencilLine as PenLine,
  PencilSimple as Pencil,
  Phone,
  Percent,
  PicnicTable,
  PiggyBank,
  Pill,
  Pause,
  Printer,
  QrCode,
  Receipt,
  Receipt as ReceiptText,
  Ruler,
  Barcode as ScanBarcode,
  Scan as ScanSearch,
  MagnifyingGlass as Search,
  PaperPlaneTilt as Send,
  GearSix as Settings,
  ShareNetwork as Share2,
  ShieldCheck,
  ShieldWarning as ShieldAlert,
  TShirt as Shirt,
  Tote as ShoppingBag,
  Basket as ShoppingBasket,
  ShoppingCartSimple as ShoppingCart,
  SlidersHorizontal,
  DeviceMobile as Smartphone,
  Stamp,
  Sparkle as Sparkles,
  Storefront as Store,
  Sun,
  CameraRotate as SwitchCamera,
  DeviceTablet as Tablet,
  Timer,
  Trash as Trash2,
  TrendUp as TrendingUp,
  Truck,
  ArrowUUpLeft as Undo2,
  UploadSimple as Upload,
  UserCircle as UserRound,
  UserPlus,
  UsersThree as Users,
  ForkKnife as UtensilsCrossed,
  Wallet,
  Warehouse,
  Wine,
  Wrench,
  XCircle,
} from '@phosphor-icons/react'

/** A glyph that is only a line (an arrow, a tick, a cross) never takes the two-tone fill. */
function line(PhIcon: Icon): Icon {
  const Line = (props: IconProps) => <PhIcon weight="regular" {...props} />
  Line.displayName = PhIcon.displayName
  return Line as Icon
}

export const ArrowDown = line(PhArrowDown)
export const ArrowLeft = line(PhArrowLeft)
export const ArrowRight = line(PhArrowRight)
export const ArrowUp = line(PhArrowUp)
export const Check = line(PhCheck)
export const ChevronDown = line(CaretDown)
export const ChevronLeft = line(CaretLeft)
export const ChevronRight = line(CaretRight)
export const ChevronUp = line(CaretUp)
export const Ellipsis = line(DotsThree)
/** The handle a row is dragged by. */
export const GripVertical = line(DotsSixVertical)
export const ExternalLink = line(ArrowSquareOut)
export const Loader2 = line(CircleNotch)
export const Maximize = line(ArrowsOut)
export const Minus = line(PhMinus)
export const MoreHorizontal = line(DotsThree)
export const Plus = line(PhPlus)
export const RefreshCw = line(ArrowsClockwise)
export const RotateCcw = line(ArrowCounterClockwise)
export const X = line(PhX)
